import { claudeModelName } from '@/features/llm-profiles/claudeModelCatalog';
import { inPeriod } from '@/features/workspace-usage/limitWindow';
import { monthLabel } from '@/features/workspace-usage/periodCopy';
import { PeriodType } from '@/lib/api/generated/model';
import type {
  AIModelEntry,
  AIModelRuns,
  PeriodBounds,
} from '@/lib/api/generated/model';

/** "1 run", "12 runs", or "12 runs (2 not measured)" (spec 215 US2, wording 234). */
export function runsLine(runs: AIModelRuns): string {
  const count = `${String(runs.run_count)} ${runs.run_count === 1 ? 'run' : 'runs'}`;
  return runs.unmeasured_run_count > 0
    ? `${count} (${String(runs.unmeasured_run_count)} not measured)`
    : count;
}

/** What a card says when its profile did not run in the period. */
export function noRunsLine(period: PeriodBounds): string {
  if (period.period_type !== PeriodType.monthly)
    return 'No runs in this period';
  return period.is_current
    ? 'No runs this month'
    : `No runs in ${monthLabel(period.key)}`;
}

/** A model the runs used: its friendly name, its id as written, or none recorded. */
export function modelLabel(modelKey: string | null): string {
  if (modelKey === null) return 'Unknown model';
  return claudeModelName(modelKey) ?? modelKey;
}

/**
 * Whether a card lists the models its runs used (research R3).
 *
 * Only when the list says something the profile line does not: more than one model,
 * or a model other than the profile's own. The "not known" card has no profile line,
 * so it always lists them.
 */
export function showsModelLines(entry: AIModelEntry): boolean {
  if (entry.runs.run_count === 0 || entry.by_model.length === 0) return false;
  if (entry.profile === null) return true;
  return (
    entry.by_model.length > 1 ||
    entry.by_model[0].model_key !== entry.profile.model
  );
}

/** The four token counts of a period's runs, summed (spec 218). */
export function tokenTotal(runs: AIModelRuns): number {
  return (
    runs.input_tokens +
    runs.output_tokens +
    runs.cache_read_tokens +
    runs.cache_creation_tokens
  );
}

/**
 * The tokens a run's own work took — input plus output (spec 220 R4). Cache reads
 * are re-read context, cheap and usually most of the total, so leading with the sum
 * of all four overstates how much the model did.
 */
export function workTokens(runs: AIModelRuns): number {
  return runs.input_tokens + runs.output_tokens;
}

/**
 * The caveat on runs that returned no token counts, in the words and tone the Spend
 * tab uses for the same runs (spec 234 US4).
 */
export function unmeasuredChipText(count: number): string {
  return `${String(count)} not measured`;
}

export const UNMEASURED_HINT =
  'Runs that returned no token counts, so their tokens are not counted here.';

/** Under an idle profile's name (spec 252): "Not used in October". */
export function notUsedLine(period: PeriodBounds): string {
  const when = inPeriod(period);
  return when === null ? 'Never used' : `Not used ${when}`;
}

/** The map's folded row (spec 252): "6 workflows did not run in October". */
export function idleWorkflowsLine(count: number, period: PeriodBounds): string {
  const noun = count === 1 ? '1 workflow' : `${String(count)} workflows`;
  const when = inPeriod(period);
  if (when === null) return `${noun} ${count === 1 ? 'has' : 'have'} never run`;
  return `${noun} did not run ${when}`;
}
